import config from 'config';
import firebase from 'firebase';
const db = firebase.database();
import isEmail from 'validator/lib/isEmail';

export const signup = ({ email, password, firstName, lastName }) => {
  return new Promise(async (resolve, reject) => {
    const bodyParams = {
      client_id: config.authClientID,
      connection: config.authRealm,
      user_metadata: { firstName, lastName },
      email,
      password,
      scope: 'openid profile email',
    };

    const respPromise = await fetch(
      `https://${config.authDomain}/dbconnections/signup`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyParams),
      }
    )

    const response = await respPromise.json();

    const { code, _id, error } = response;

    if (error) {
      console.log('AuthDomain signup - error', error);
      resolve({ error });
    } else if (code === 'user_exists') {
      console.log('AuthDomain signup - user_exists');
      resolve({
        error: 'Existing Trybe account found. Please log in instead.',
      });
    } else if (_id) {
      let userId = _id;
      //if userId doesn't include facebook, or doesn't include auth0, add auth0
      if (!userId.includes('facebook') && !userId.includes('auth0')) {
        userId = 'auth0|' + _id;
      }

      // console.log('AuthDomain signup success. userId', userId);
      resolve({ userId });
    }
  });
};

export const resetPassword = ({ email }) => {
  return new Promise(async (resolve, reject) => {
    const bodyParams = {
      client_id: config.authClientID,
      connection: config.authRealm,
      email,
    };

    const response = await fetch(
      `https://${config.authDomain}/dbconnections/change_password`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(bodyParams),
      }
    );

    const statusCode = response.status;
    if (statusCode === 200) {
      resolve({
        statusCode,
      });
    } else if (response.error) {
      resolve({
        error: response.error,
        error_description: response.error_description,
      });
    }
  });
};

export const deleteUser = ({ email, password }) => {
  return new Promise(async (resolve, reject) => {
    const { accessToken, error, error_description } = await getAccessToken({
      email,
      password,
    })

    if (!accessToken || error) {
      reject({
        error: 'Failed Login',
        error_msg:
          'No account found for that email & password combination. Please check your credentials or contact support@trybe.do',
      })
    }
    const id = db.ref(`requests/deleteUser`).push().key
    const path = `requests/deleteUser/${id}`
    const listenerPath = `${path}/response`

    db.ref(listenerPath).on('value', s => {
      const val = s.val()

      if(!val) return

      if (val.success) {
        db.ref(listenerPath).off()
        resolve({ success: true })
      } else if (val.error) {
        db.ref(listenerPath).off()

        reject({
          error: 'Delete account error.',
          error_msg: 'Unable to delete account. Please ensure all of your subscriptions have been canceled or contact support@trybe.do for more assistance.'
        })
      }
    })

    await db.ref(path).set({ accessToken })
  })
}

export const verifyEmail = ({ email, coachOrgId }) => {
  return new Promise(async (resolve, reject) => {
    if (!email) {
      reject({
        error: 'Invalid email',
        error_msg:
          'No email provided',
      })
    }

    const id = db.ref(`requests/verifyEmail`).push().key
    const path = `requests/verifyEmail/${id}`
    const listenerPath = `${path}/response`

    db.ref(listenerPath).on('value', s => {
      const val = s.val()

      if(!val) return

      if (val.success) {
        db.ref(listenerPath).off()
        resolve({ success: true })
      } else if (val.error) {
        db.ref(listenerPath).off()

        reject({
          error: 'Verify email error',
          error_msg: 'Unable to send verification email. Please double check your account email or contact support@trybe.do'
        })
      }
    })

    const payload = coachOrgId ? { email, coachOrgId } : { email }

    await db.ref(path).set(payload)
  })
}

export const getAccessToken = ({ email, password }) => {
  return new Promise(async (resolve, reject) => {
    const bodyParams = {
      client_id: config.authClientID,
      grant_type: config.authGrantType,
      username: email,
      password: password,
      realm: config.authRealm,
      scope: 'openid profile email',
    };

    const respPromise = await fetch(
      `https://${config.authDomain}/oauth/token`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyParams),
      }
    );

    const resp = await respPromise.json();

    if (resp.error) {
      console.log('AuthDomain getAccessToken error', resp.error);
      resolve({
        error: resp.error,
        error_description: resp.error_description,
      });
    } else {
      resolve({
        accessToken: resp.access_token,
      });
    }
  });
};

export const getUserInfo = async (accessToken) => {
  const respPromise = await fetch(`https://${config.authDomain}/userinfo`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  const resp = await respPromise.json();
  // console.log('getUserInfo resp', resp);

  return {
    userId: resp.sub,
    auth0Pf: resp,
  };
};

export const getFirebaseCustomToken = (userId) => {
  return new Promise(async (resolve) => {
    const authKey = db.ref('requests/authWithCustomToken').push().key;
    const authPath = `requests/authWithCustomToken/${authKey}`;
    const authListenerPath = `requests/authWithCustomToken/${authKey}/response`;

    const listener = db.ref(authListenerPath).on('value', (snap) => {
      const response = snap.val();
      const token = response ? response.token : null;

      if (token) {
        db.ref(authListenerPath).off('value', listener);
        resolve(token);
      }
    });

    db.ref(`${authPath}/uid`).set(userId);
  });
};

export const firebaseAuthWithCustomToken = async (token) => {
  try {
    const result = await firebase.auth().signInWithCustomToken(token);
    return result.uid;
  } catch (err) {
    console.log('firebaseAuthWithCustomToken err', err);
    throw err;
  }
};

export const dbAuth = async (userId) => {
  try {
    const token = await getFirebaseCustomToken(userId);
    const auth = await firebaseAuthWithCustomToken(token);
    return auth;
  } catch (err) {
    console.log('dbAuth err', err);
  }
};

// VALIDATORS
const standardizeEmail = (text) => {
  if (!text || !text.length) {
    return null;
  }

  return text.trim().toLowerCase();
};

export const checkEmailValid = (text) => {
  if (!text || !text.length) {
    return false;
  }

  const standardized = standardizeEmail(text);
  return isEmail(standardized);
};

export const checkPwValid = (text) => {
  if (text && text.length > 5) {
    return true;
  } else {
    return false;
  }
};



// WEBPACK FOOTER //
// ./src/domains/AuthDomain.js